import { inject } from 'vue'
import { BaseHttpService } from './base-http-service'
import { UserInformation } from '@/types'

class AuthService extends BaseHttpService {
    readonly authAdmin = inject('authAdmin') as boolean

    private getUri(uri: string) {
        return `${this.authAdmin ? '/admin' : ''}${uri}`
    }

    async isAuth() {
        if(!this.authStore.getIsAuth()) {
            const result = await this.doApiGet<{isAuthenticated: boolean}>(this.getUri('/auth/is-auth'))
            this.authStore.setIsAuth(result.success && (result.response?.isAuthenticated ?? false))
        }
        return this.authStore.getIsAuth()
    }

    async adminCartLoaded() {
        if(!this.authStore.getAdminCartLoaded()) {
            const result = await this.doApiGet<{adminCartLoaded: boolean}>(this.getUri('/auth/admin-cart-loaded'))
            this.authStore.setAdminCartLoaded(result.success && (result.response?.adminCartLoaded ?? false))
        }
        return this.authStore.getAdminCartLoaded()
    }

    async login(username: string, password: string) {
        const result = await this.doApiPost<{success: boolean, message?: string}>(this.getUri('/auth/login'), {username, password})
        if (result.success) {
            this.authStore.setIsAuth(true)
        }
        return result
    }

    async getUser(): Promise<UserInformation> {
        if(!this.authStore.getUser()) {
            const result = await this.doApiGet<UserInformation>('/auth/profile')
            if (result.success) {
                this.authStore.setUser(result.response!)
            } else {
                throw new Error(result.error!.message)
            }
        }
        return this.authStore.getUser()!
    }

    async logout() {
        await this.doApiPost<any>('/auth/logout')
        this.authStore.clearAuthSession()
    }

    async requestPasswordReset(email: string) {
        const result = await this.doApiPost<{ success: boolean }>('/auth/request-password-reset', {
            email
        })
        if (result.success) {
            return result.response!
        }
        throw new Error(result.error!.message)
    }

    async passwordReset(uidb36: string, token: string, password1: string, password2: string) {
        const result = await this.doApiPost<{ success: boolean, message: string }>('/auth/password-reset', {
            uidb36,
            token,
            password1,
            password2,
        })
        if (result.success) {
            return result.response!
        }
        throw new Error(result.error!.message)
    }
}

export function useAuthService() {
    return new AuthService()
}
